<template>
  <v-row>
    <v-col cols="12">
      <div :id="id" class="editor-container">
        <VueEditor
          v-model="internalEditorContent"
          content-type="html"
          :editor-toolbar="
            customToolbarOptions ? customToolbarOptions : toolbarOptions
          "
          :id="id"
          @image-added="handleImageAdded"
          :useCustomImageHandler="useCustomImageHandler"
          ref="richTextEditor"
        />
      </div>
      <v-textarea
        v-model="internalEditorContent"
        class="d-none"
        :rules="rules"
        :id="id"
      ></v-textarea>
      <span class="rich-text-error-message">{{
        richTextPropError?.messagesToDisplay[0]
      }}</span>
    </v-col>

    <v-dialog v-model="videoDialog" max-width="500px">
      <v-card class="pa-4">
        <v-card-text class="py-1 ma-0 my-1">
          <h3 class="primary--text">
            {{ $t("common.rich-text.title-video") }}
          </h3>
        </v-card-text>
        <v-card-text>
          <v-text-field
            :label="$t('common.rich-text.input-video')"
            v-model="videoUrl"
            outlined
            hide-details="auto"
            persistent-placeholder
            :error-messages="hasErrorMessage"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="justify-end d-flex flex-wrap" style="gap: 8px">
          <v-btn class="primary white--text" outlined @click="insertVideoUrl">{{
            $t("common.rich-text.insert-video")
          }}</v-btn>
          <v-btn class="primary--text" outlined @click="closeDialog">{{
            $t("common.rich-text.cancel-video")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mapActions } from "vuex";
import { Quill, VueEditor } from "vue2-editor";

const AlignClass = Quill.import("attributors/class/align");
const AlignStyle = Quill.import("attributors/style/align");
Quill.register(AlignClass, true);
Quill.register(AlignStyle, true);

export default {
  name: "RichTextEditor",
  components: {
    VueEditor,
  },
  props: {
    form: {
      type: Object,
      default: () => ({}),
    },
    rules: {
      type: Array,
      default: () => [],
    },
    id: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: "",
    },
    useCustomImageHandler: {
      type: Boolean,
      default: false,
    },
    customToolbarOptions: {
      type: Array,
    },
    inlineStyles: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      internalEditorContent: this.value,
      videoDialog: false,
      videoUrl: "",
      hasErrorMessage: "",
      toolbarOptions: [
        ["link", "image", "video"],
        ["bold", "italic", "underline"],
        [{ align: [] }],
        [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ color: [] }],
        [{ size: [] }],
        ["clean"],
      ],
    };
  },
  computed: {
    richTextPropError() {
      return this.form?.inputs?.find(e => e.id === this.id);
    },
    hasError() {
      return this.richTextPropError?.hasError;
    },
    errorMessage() {
      return !this.richTextPropError?.messagesToDisplay[0];
    },
  },
  watch: {
    errorMessage() {
      this.validateEditor();
    },
    internalEditorContent(newValue) {
      if (newValue === "<p><br></p>") {
        this.internalEditorContent = "";
      }
      this.$emit("input", newValue);
    },
    value(newValue) {
      this.internalEditorContent = newValue;
    },
  },
  methods: {
    ...mapActions(["showSnackbarError"]),
    isValidVideoUrl(url) {
      const videoRegex =
        /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be|vimeo\.com)\/.+$/;
      return videoRegex.test(url);
    },
    getEmbedUrl(url) {
      let embedUrl = url;
      if (url.includes("youtube.com/watch?v=")) {
        const videoId = url.split("v=")[1];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      } else if (url.includes("youtu.be/")) {
        const videoId = url.split("youtu.be/")[1];
        embedUrl = `https://www.youtube.com/embed/${videoId}`;
      } else if (url.includes("vimeo.com/")) {
        const videoId = url.split("vimeo.com/")[1];
        embedUrl = `https://player.vimeo.com/video/${videoId}`;
      }

      return embedUrl;
    },
    openVideoDialog() {
      this.videoDialog = true;
    },
    closeDialog() {
      this.videoDialog = false;
      this.videoUrl = "";
    },
    insertVideoUrl() {
      if (!this.isValidVideoUrl(this.videoUrl)) {
        this.hasErrorMessage = this.$t("common.rich-text.invalid-video");
      }
      if (this.isValidVideoUrl(this.videoUrl)) {
        const editor = this.$refs.richTextEditor.quill;
        const range = editor.selection.savedRange.index;
        const embedUrl = this.getEmbedUrl(this.videoUrl);
        editor.insertEmbed(range, "video", embedUrl, "user");
        editor.setSelection(range + 1);
        this.$nextTick(() => {
          this.closeDialog();
        });
      } else {
        this.showSnackbarError(this.$t("common.rich-text.invalid-video"));
      }
    },
    validateEditor() {
      const editorContainer = document.querySelector(
        `.editor-container#${this.id}`
      );

      if (this.hasError && !this.errorMessage) {
        editorContainer.classList.add("error-border");
        editorContainer.classList.remove("normal-border");
      } else {
        editorContainer.classList.add("normal-border");
        editorContainer.classList.remove("error-border");
      }
    },
    handleImageAdded(file, editor, cursorLocation) {
      this.$emit("image-added", file, editor, cursorLocation);
    },
    addInlineStyles() {
      const editor = this.$refs.richTextEditor.quill;

      editor.on("text-change", () => {
        const elements = editor.root.querySelectorAll(
          ".ql-size-small, .ql-size-large, .ql-size-huge"
        );

        elements.forEach(element => {
          if (element.classList.contains("ql-size-small")) {
            element.style.fontSize = ".75em";
          }
          if (element.classList.contains("ql-size-large")) {
            element.style.fontSize = "1.5em";
          }
          if (element.classList.contains("ql-size-huge")) {
            element.style.fontSize = "2.5em";
          }
        });

        this.internalEditorContent = editor.root.innerHTML;
      });
    },
  },
  mounted() {
    const editor = this.$refs.richTextEditor.quill;

    if (editor) {
      const toolbar = editor.getModule("toolbar");

      if (this.inlineStyles) this.addInlineStyles();

      toolbar.addHandler("video", () => {
        this.openVideoDialog();
      });
      toolbar.addHandler("link", () => {
        let range = editor.getSelection();

        if (range == null || range.length === 0) return;
        const tooltip = editor.theme.tooltip;

        tooltip.save = () => {
          const inputValue = tooltip.textbox.value;

          const regex = /^https:\/\/[^\s$.?#].[^\s]*$/;

          if (inputValue.match(regex)) {
            editor.format("link", inputValue);
          } else {
            this.showSnackbarError(this.$t("common.rich-text.invalid-url"));
          }
        };
        tooltip.edit("link", "");
      });
    }
  },
};
</script>

<style>
.editor-container {
  position: relative;
}

.ql-container {
  height: 200px !important;
  background-color: #fff !important;
  border-radius: 0px 0px 10px 10px;
  border-color: #ccc;
}

.ql-toolbar {
  border-radius: 10px 10px 0px 0px;
  border-color: #ccc;
}

.rich-text-error-message {
  color: red;
  font-size: 14px;
}

.error-border {
  border: 1px solid red !important;
  border-radius: 10px !important;
}

.normal-border {
  border-color: #ccc !important;
}
</style>
